import { useRef, useCallback, useEffect } from 'react';
import { throttleToAnimationFrame } from '@utils/common';
import { containerCls, canvasCls } from './LegacyCanvasDrawing.style.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { drawLegacyCanvasDrawing } from '../../rasterizer/drawLegacyCanvasDrawing.js';
import { useScale } from '../hooks/useScale.js';

const LegacyCanvasDrawing = ({
  sheetRef,
  pageId,
  pageAspectRatio,
  zIndex,
  item,
  isSelected,
  isCloned,
  onSelect,
  Before,
  After
}) => {
  const scale = useScale();
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const handleSelect = useCallback(evt => {
    if (onSelect !== undefined) {
      onSelect(evt, item);
    }
  }, [item, onSelect]);
  const overflowingStrokeWidth = item.style.stroke.width / 2 * scale;
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (container === null || canvas === null) {
      return;
    }
    const ctx = canvas.getContext("2d");
    if (ctx === null) {
      return;
    }
    const drawShape = () => {
      const overflowingWidth = container.offsetWidth + overflowingStrokeWidth * 2;
      const overflowingHeight = container.offsetHeight + overflowingStrokeWidth * 2;
      canvas.width = overflowingWidth * window.devicePixelRatio;
      canvas.height = overflowingHeight * window.devicePixelRatio;
      drawLegacyCanvasDrawing({
        ctx,
        canvasSize: {
          width: canvas.width,
          height: canvas.height
        },
        strokeWidth: item.style.stroke.width * window.devicePixelRatio * scale,
        item: {
          ...item,
          // enforce complete takeover of the canvas, as we've scaled it
          // specifically to the defined bounding box of the item already
          position: {
            x: overflowingStrokeWidth / overflowingWidth,
            y: overflowingStrokeWidth / overflowingHeight
          },
          size: {
            width: 1 - overflowingStrokeWidth * 2 / overflowingWidth,
            height: 1 - overflowingStrokeWidth * 2 / overflowingHeight
          }
        }
      });
    };
    drawShape();
    const handleResize = throttleToAnimationFrame(drawShape);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, scale]);
  useEffect(() => {
    const node = containerRef.current;
    if (node === null) {
      return;
    }
    node.addEventListener("focusin", handleSelect, {
      passive: false
    });
    node.addEventListener("mousedown", handleSelect, {
      passive: false
    });
    node.addEventListener("touchstart", handleSelect, {
      passive: false
    });
    return () => {
      node.removeEventListener("focusin", handleSelect);
      node.removeEventListener("mousedown", handleSelect);
      node.removeEventListener("touchstart", handleSelect);
    };
  }, [item, handleSelect]);
  return /*#__PURE__*/jsxs("div", {
    ref: containerRef,
    className: containerCls,
    "data-id": item.id,
    style: {
      zIndex,
      left: `${(item.position.x * 100).toString()}%`,
      top: `${(item.position.y * 100).toString()}%`,
      width: `${(item.size.width * 100).toString()}%`,
      height: `${(item.size.height * 100).toString()}%`
    },
    children: [Before !== undefined && /*#__PURE__*/jsx(Before, {
      sheetRef: sheetRef,
      pageId: pageId,
      pageAspectRatio: pageAspectRatio,
      isSelected: isSelected,
      isCloned: isCloned,
      item: item
    }), /*#__PURE__*/jsx("canvas", {
      ref: canvasRef,
      className: canvasCls,
      style: {
        transform: `translate(-${overflowingStrokeWidth.toString()}px, -${overflowingStrokeWidth.toString()}px)`,
        width: `calc(100% + ${(overflowingStrokeWidth * 2).toString()}px)`,
        height: `calc(100% + ${(overflowingStrokeWidth * 2).toString()}px)`
      }
    }), After !== undefined && /*#__PURE__*/jsx(After, {
      sheetRef: sheetRef,
      pageId: pageId,
      pageAspectRatio: pageAspectRatio,
      isSelected: isSelected,
      isCloned: isCloned,
      item: item
    })]
  });
};

export { LegacyCanvasDrawing };
//# sourceMappingURL=LegacyCanvasDrawing.js.map
